import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { MarkdownEditor } from '../ui/markdown-editor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { DatePickerWithMarkers } from '../ui/date-picker-with-markers';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useAuth } from '../../contexts/AuthContext';
import { useProductDateCache } from '../../contexts/ProductDateCacheContext';
import { initiativesApi, teamApi, epicsApi, productTagsApi } from '../../services/api';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import EpicDetailModal from '../epic/EpicDetailModal';
import FeatureDetailModal from '../product/FeatureDetail';
import { UnifiedCoPilot } from '../copilot';
import { ModalZIndexProvider } from '../../contexts/ModalZIndexContext';
import { useEntityModal } from '../../contexts/EntityModalContext';
import { NotesPanel, AttachmentsPanel } from '../shared';
import KpiSubscriptionTab from '../shared/KpiSubscriptionTab';
import { 
  Rocket, Calendar, AlertTriangle, Target, User, 
  Loader2, Save, Clock, BarChart3, 
  Trash2, Check, Eye, Pencil, ListPlus, Plus, ExternalLink, X,
  Tag, FileText, Search, Filter, Paperclip, MessageSquare
} from 'lucide-react';

// Status options for initiatives
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-500' },
  { value: 'planning', label: 'Planning', color: 'bg-blue-500' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-500' },
  { value: 'shipped', label: 'Shipped', color: 'bg-green-500' },
  { value: 'paused', label: 'Paused', color: 'bg-purple-500' },
];

// Confidence options for target date
const CONFIDENCE_OPTIONS = [
  { value: 'high', label: 'High', color: 'text-green-600 dark:text-green-400' },
  { value: 'medium', label: 'Medium', color: 'text-amber-600 dark:text-amber-400' },
  { value: 'low', label: 'Low', color: 'text-red-600 dark:text-red-400' },
];

// Delete Confirmation Modal Component
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, title, isDeleting }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={cn("relative z-10 p-6 rounded-lg shadow-xl max-w-md w-full mx-4", colors.bgCard)}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Delete Initiative</h3>
        </div>
        <p className={cn("text-sm mb-2", colors.textSecondary)}>
          Are you sure you want to delete this initiative?
        </p>
        <p className={cn("text-sm font-medium mb-4 p-2 rounded", colors.bgTertiary, colors.textPrimary)}>
          &ldquo;{title}&rdquo;
        </p>
        <p className={cn("text-xs mb-6", colors.textMuted)}>
          This action cannot be undone. All linked epics will also be deleted.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Delete Initiative
          </Button>
        </div>
      </div>
    </div>
  );
};

// Add Epic Modal Component - supports both linking existing and creating new
const AddEpicModal = ({ isOpen, onClose, onSave, productId, initiativeId, existingEpicIds = [] }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [activeTab, setActiveTab] = useState('link'); // 'link' or 'create'
  const [availableEpics, setAvailableEpics] = useState([]);
  const [selectedEpicId, setSelectedEpicId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Create new epic form
  const [newEpicTitle, setNewEpicTitle] = useState('');
  const [newEpicDescription, setNewEpicDescription] = useState('');
  
  const selectedEpic = availableEpics.find(e => e.epic_id === selectedEpicId);
  
  // Load available epics from the product
  useEffect(() => {
    const loadEpics = async () => {
      if (!isOpen || !productId) return;
      
      setLoading(true);
      try {
        const res = await epicsApi.listByProduct(productId);
        // Filter out epics already linked to any initiative
        const unlinkedEpics = (res.data || []).filter(
          epic => !existingEpicIds.includes(epic.epic_id) && !epic.initiative_id
        );
        setAvailableEpics(unlinkedEpics);
      } catch (err) {
        console.error('Failed to load epics:', err);
        setAvailableEpics([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadEpics();
  }, [isOpen, productId, existingEpicIds]);
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedEpicId('');
      setNewEpicTitle('');
      setNewEpicDescription('');
      setActiveTab('link');
    }
  }, [isOpen]);
  
  // Link existing epic
  const handleLink = async () => {
    if (!selectedEpicId || !initiativeId) return;
    
    setSaving(true);
    try {
      await epicsApi.linkToInitiative(selectedEpicId, initiativeId);
      toast.success('Epic linked to initiative');
      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to link epic:', err);
      toast.error('Failed to link epic');
    } finally {
      setSaving(false);
    }
  };
  
  // Create new epic
  const handleCreate = async () => {
    if (!newEpicTitle.trim() || !initiativeId) return;
    
    setSaving(true);
    try {
      await epicsApi.createForInitiative(initiativeId, {
        title: newEpicTitle.trim(),
        description: newEpicDescription.trim(),
        status: 'Not Started'
      });
      toast.success('Epic created and linked to initiative');
      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to create epic:', err);
      toast.error('Failed to create epic');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={cn("relative z-10 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4", colors.bgCard)}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={cn("text-lg font-semibold flex items-center gap-2", colors.textPrimary)}>
            <Rocket className="w-5 h-5" />
            Add Epic
          </h3>
          <button
            onClick={onClose}
            className={cn("p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700", colors.textMuted)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className={cn("flex gap-1 mb-4 p-1 rounded-lg", colors.bgTertiary)}>
          <button
            onClick={() => setActiveTab('link')}
            className={cn(
              "flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors",
              activeTab === 'link' 
                ? cn(colors.bgCard, colors.textPrimary, "shadow-sm")
                : colors.textMuted
            )}
          >
            Link Existing
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={cn(
              "flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors",
              activeTab === 'create' 
                ? cn(colors.bgCard, colors.textPrimary, "shadow-sm")
                : colors.textMuted
            )}
          >
            Create New
          </button>
        </div>
        
        {/* Link Existing Tab */}
        {activeTab === 'link' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
              </div>
            ) : availableEpics.length === 0 ? (
              <div className={cn("text-center py-8", colors.textMuted)}>
                <Rocket className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No unlinked epics available</p>
                <p className="text-xs mt-1">Create a new epic instead</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={() => setActiveTab('create')}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Create New Epic
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label className={colors.textSecondary}>Select Epic</Label>
                  <Select value={selectedEpicId} onValueChange={setSelectedEpicId}>
                    <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}>
                      <SelectValue placeholder="Choose an epic to link..." />
                    </SelectTrigger>
                    <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                      {availableEpics.map(epic => (
                        <SelectItem key={epic.epic_id} value={epic.epic_id}>
                          <div className="flex items-center gap-2">
                            <span>{epic.title}</span>
                            <span className={cn(
                              "text-xs px-1.5 py-0.5 rounded",
                              epic.status === 'Done' ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" :
                              epic.status === 'In Progress' ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" :
                              "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                            )}>
                              {epic.status}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Epic Description Preview */}
                {selectedEpic && (
                  <div className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                    <Label className={cn("text-xs mb-1 block", colors.textMuted)}>Epic Description</Label>
                    <p className={cn("text-sm", colors.textSecondary)}>
                      {selectedEpic.description || 'No description provided'}
                    </p>
                  </div>
                )}
                
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={onClose} disabled={saving}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleLink}
                    disabled={!selectedEpicId || saving}
                    className={cn(colors.accentGradient)}
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                    Link Epic
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
        
        {/* Create New Tab */}
        {activeTab === 'create' && (
          <div className="space-y-4">
            <div>
              <Label className={colors.textSecondary}>Epic Title *</Label>
              <Input
                value={newEpicTitle}
                onChange={(e) => setNewEpicTitle(e.target.value)}
                placeholder="e.g., User Authentication"
                className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
              />
            </div>
            
            <div>
              <Label className={colors.textSecondary}>Description</Label>
              <Textarea
                value={newEpicDescription}
                onChange={(e) => setNewEpicDescription(e.target.value)}
                placeholder="Describe the epic's purpose and scope..."
                rows={3}
                className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreate}
                disabled={!newEpicTitle.trim() || saving}
                className={cn(colors.accentGradient)}
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                Create Epic
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * InitiativeDetailModal
 * 
 * Displays and allows editing of initiative details with linked epics.
 * 
 * STANDALONE MODE:
 * When `standalone={true}`, this modal operates independently without requiring
 * parent callbacks. Used by Executive AI to open initiatives in-place without
 * navigating away. In standalone mode:
 * - onSave/onDelete callbacks are ignored (API is called directly, toast shown)
 * - Modal closes automatically after save/delete
 * - No parent state refresh needed
 */
const InitiativeDetailModal = ({ 
  isOpen, 
  onClose, 
  initiative, 
  goals = [], 
  personas = [],
  productContext = {},
  onSave,
  onSaveAndClose,
  onDelete,
  onOpenEpic,
  onGoalChange,  // Called when goals are linked/unlinked
  onKpiChange,   // Called when KPI subscriptions change (for cascading refresh)
  mode = 'edit', // 'edit' | 'create'
  standalone = false,  // When true, modal operates independently without parent callbacks
  zIndex = 50,  // Z-index for modal stacking
  suppressChildModals = false,  // When true, hides AI copilot and prevents entity edit modals from launching as children
  parentRefresh,  // Parent's refresh callbacks — when present, this modal is a child and calls these after save
}) => {
  const { getThemeColors } = useSafeMode();
  const { canCrudProducts, canUseAI } = usePermissions();
  const { user } = useAuth();
  const colors = getThemeColors();
  const { registerParentCallbacks, unregisterParentCallbacks } = useEntityModal();
  
  // Permission flags
  const canEdit = canCrudProducts;
  const canUseAICopilot = canUseAI;
  
  // Edit mode toggle (for non-draft statuses)
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Track collections dirtied by child modal saves — flushed as stale on close
  const dirtyCollectionsRef = useRef(new Set());
  const [showAddEpicModal, setShowAddEpicModal] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedEpicForModal, setSelectedEpicForModal] = useState(null); // Epic modal over Initiative modal
  
  // Track if KPI subscriptions were modified during this edit session (for cascading refresh)
  const [kpisModified, setKpisModified] = useState(false);
  
  // Track original follow_type for detecting mode switches (for cascading refresh)
  const [originalFollowType, setOriginalFollowType] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'draft',
    goal_ids: [],  // Multi-goal support
    target_date: '',
    date_confidence: 'medium',
    is_blocked: false,
    blocker_reason: '',
    progress_mode: 'auto',
    manual_progress_percent: 0,
    owner_id: '',
    follow_type: 'epics',
    tag_ids: [],
    subscribed_kpi_ids: [],  // KPI subscriptions
  });
  
  // Show AI Co-pilot panel (only for existing initiatives in draft status OR edit mode with permission)
  // Non-draft initiatives have locked fields, but edit mode unlocks them for AI suggestions
  // This reacts to formData.status and isEditMode changes in real-time (same as Epics)
  const showAICopilot = mode !== 'create' && canUseAICopilot && !suppressChildModals && initiative?.initiative_id && (formData.status === 'draft' || isEditMode);
  
  const [teamMembers, setTeamMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [targetDateError, setTargetDateError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Linked Epics state
  const [linkedEpics, setLinkedEpics] = useState([]);
  const [loadingEpics, setLoadingEpics] = useState(false);
  
  // Goals tab state
  const [goalSearch, setGoalSearch] = useState('');
  const [showGoalDropdown, setShowGoalDropdown] = useState(false);
  
  // Ref for goal dropdown click-outside handling
  const goalDropdownRef = useRef(null);
  
  // Tags mode state
  const [availableTags, setAvailableTags] = useState([]);
  const [ownedTags, setOwnedTags] = useState([]); // Tags owned by this initiative
  const [taggedFeatures, setTaggedFeatures] = useState([]);
  const [loadingTaggedFeatures, setLoadingTaggedFeatures] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [featureSearchQuery, setFeatureSearchQuery] = useState('');
  const [featureEpicFilter, setFeatureEpicFilter] = useState('all');
  const [selectedFeatureForModal, setSelectedFeatureForModal] = useState(null);
  
  // Tag CRUD state
  const [showCreateTagModal, setShowCreateTagModal] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const [editingTag, setEditingTag] = useState(null);
  const [deletingTagId, setDeletingTagId] = useState(null);
  
  // Follow type switch state
  const [showFollowTypeSwitchConfirm, setShowFollowTypeSwitchConfirm] = useState(false);
  const [followTypeSwitchImpact, setFollowTypeSwitchImpact] = useState(null);
  const [pendingFollowType, setPendingFollowType] = useState(null);
  
  // Filter subscribed_kpi_ids when goal_ids changes
  // Removes KPIs that belong to goals that are no longer linked
  useEffect(() => {
    if (!formData.subscribed_kpi_ids?.length) return;
    
    const currentGoalIds = new Set(formData.goal_ids || []);
    const filteredKpis = formData.subscribed_kpi_ids.filter(kpiKey => {
      // KPI key format is "goal_id:kpi_id"
      const goalId = kpiKey.split(':')[0];
      return currentGoalIds.has(goalId);
    });
    
    // Only update if there were KPIs removed
    if (filteredKpis.length !== formData.subscribed_kpi_ids.length) {
      setFormData(prev => ({
        ...prev,
        subscribed_kpi_ids: filteredKpis
      }));
      // Mark as changed since we removed orphaned KPIs
      if (filteredKpis.length < formData.subscribed_kpi_ids.length) {
        setHasChanges(true);
      }
    }
  }, [formData.goal_ids]); // Only trigger on goal_ids changes
  
  // Computed: Are goals linked? Used for tab locking
  const hasLinkedGoals = formData.goal_ids && formData.goal_ids.length > 0;
  
  // Computed: Parent deadlines for calendar (linked Goals' target dates)
  const parentDeadlines = useMemo(() => {
    const deadlines = [];
    if (formData.goal_ids && goals) {
      formData.goal_ids.forEach(gid => {
        const goal = goals.find(g => g.goal_id === gid);
        if (goal?.target_date) {
          deadlines.push({
            date: goal.target_date,
            label: `Goal: ${goal.title || 'Deadline'}`,
          });
        }
      });
    }
    return deadlines;
  }, [formData.goal_ids, goals]);
  
  // Mode switch confirmation modal
  const [showModeSwitchModal, setShowModeSwitchModal] = useState(false);
  
  // Computed: Show edit toggle? (when status !== draft and mode is edit)
  const showEditToggle = canEdit && mode === 'edit' && formData.status !== 'draft';
  
  // Computed: Is currently in editing state?
  const isEditing = canEdit && (mode === 'create' || formData.status === 'draft' || isEditMode);
  
  // Click outside handler for goal dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (goalDropdownRef.current && !goalDropdownRef.current.contains(event.target)) {
        setShowGoalDropdown(false);
      }
    };
    
    if (showGoalDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showGoalDropdown]);
  
  // Refs
  const draftSaveTimeoutRef = useRef(null);
  
  // Validation state for status change
  const [activationErrors, setActivationErrors] = useState([]);
  const [showActivationValidation, setShowActivationValidation] = useState(false);
  
  // Validate initiative can move out of draft
  const validateStatusChange = (data) => {
    const errors = [];
    
    // Must have an owner to move out of draft
    if (!data.owner_id) {
      errors.push({
        title: 'Owner Required',
        description: 'Please assign an owner to this initiative before changing status.'
      });
    }
    
    // Target date required to move out of draft
    if (!data.target_date) {
      errors.push({
        title: 'Target Date Required',
        description: 'Please set a target date for this initiative.'
      });
    }
    
    return errors;
  };
  
  // Handle form field changes
  const handleChange = (field, value) => {
    // Intercept status change away from "draft" and validate
    if (field === 'status' && value !== 'draft' && formData.status === 'draft') {
      const errors = validateStatusChange(formData);
      if (errors.length > 0) {
        setActivationErrors(errors);
        setShowActivationValidation(true);
        return; // Don't change status
      }
    }
    
    // Validate target_date against linked goal deadlines
    if (field === 'target_date' && value) {
      const selectedDate = new Date(value);
      let dateError = null;
      
      // Check against all parent deadlines (from linked goals)
      for (const deadline of parentDeadlines) {
        const goalDate = new Date(deadline.date);
        if (selectedDate > goalDate) {
          // Find the goal name from the label (format: "Goal: {title}")
          const goalName = deadline.label?.replace('Goal: ', '') || 'linked Goal';
          const formattedDate = goalDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
          dateError = `Must complete by ${formattedDate} to align with "${goalName}"`;
          break;
        }
      }
      setTargetDateError(dateError);
    }
    
    // Clear target date error if clearing the date
    if (field === 'target_date' && !value) {
      setTargetDateError(null);
    }
    
    // Re-validate target date when goals change (goal_ids)
    if (field === 'goal_ids' && formData.target_date) {
      // Goals are changing - need to recompute deadlines and validate
      // This will be picked up by the useEffect below
    }
    
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-set dateConfidence to "Low" when isBlocked becomes true
      if (field === 'is_blocked' && value === true) {
        newData.date_confidence = 'low';
      }
      
      return newData;
    });
    setHasChanges(true);
  };
  
  // Re-validate target date when parentDeadlines change (due to goal_ids change)
  React.useEffect(() => {
    if (formData.target_date && parentDeadlines.length > 0) {
      const selectedDate = new Date(formData.target_date);
      let dateError = null;
      
      for (const deadline of parentDeadlines) {
        const goalDate = new Date(deadline.date);
        if (selectedDate > goalDate) {
          const goalName = deadline.label?.replace('Goal: ', '') || 'linked Goal';
          const formattedDate = goalDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
          dateError = `Must complete by ${formattedDate} to align with "${goalName}"`;
          break;
        }
      }
      setTargetDateError(dateError);
    } else if (!formData.target_date) {
      setTargetDateError(null);
    }
  }, [parentDeadlines, formData.target_date]);
  
  // Handle AI Co-Pilot apply updates
  const handleApplyUpdates = useCallback((updates) => {
    if (!updates) return;
    
    Object.entries(updates).forEach(([field, value]) => {
      // Handle status - normalize to valid values
      if (field === 'status' && value) {
        const validStatuses = ['draft', 'planning', 'in_progress', 'shipped', 'paused'];
        const normalizedStatus = value.toLowerCase().replace(/ /g, '_');
        if (validStatuses.includes(normalizedStatus)) {
          setFormData(prev => ({ ...prev, status: normalizedStatus }));
        }
      }
      // Handle date_confidence - normalize
      else if (field === 'date_confidence' && value) {
        const validConfidences = ['high', 'medium', 'low'];
        const normalizedConfidence = value.toLowerCase();
        if (validConfidences.includes(normalizedConfidence)) {
          setFormData(prev => ({ ...prev, date_confidence: normalizedConfidence }));
        }
      }
      // Handle boolean fields
      else if (field === 'is_blocked') {
        setFormData(prev => ({ ...prev, is_blocked: Boolean(value) }));
      }
      // Regular field update (skip special operation fields)
      else if (!field.startsWith('_')) {
        setFormData(prev => ({ ...prev, [field]: value }));
      }
    });
    
    setHasChanges(true);
  }, []);
  
  // Refresh initiative data after changes
  const refreshInitiativeData = useCallback(async () => {
    if (!initiative?.initiative_id) return;
    try {
      const res = await initiativesApi.get(initiative.initiative_id);
      if (res.data) {
        // Update form with refreshed data - handle goal_ids with legacy fallback
        const goalIds = res.data.goal_ids || (res.data.goal_id ? [res.data.goal_id] : []);
        setFormData({
          title: res.data.title || '',
          description: res.data.description || '',
          status: res.data.status || 'draft',
          goal_ids: goalIds,
          target_date: res.data.target_date ? new Date(res.data.target_date).toISOString().split('T')[0] : '',
          date_confidence: res.data.date_confidence || 'medium',
          is_blocked: res.data.is_blocked || false,
          blocker_reason: res.data.blocker_reason || '',
          progress_mode: res.data.progress_mode || 'auto',
          manual_progress_percent: res.data.manual_progress_percent || 0,
          owner_id: res.data.owner_id || '',
          follow_type: res.data.follow_type || 'epics',
          tag_ids: res.data.tag_ids || [],
        });
      }
    } catch (err) {
      console.error('Failed to refresh initiative:', err);
    }
  }, [initiative?.initiative_id]);
  
  // Load team members
  useEffect(() => {
    const loadTeam = async () => {
      try {
        const res = await teamApi.list();
        const activeMembers = (res.data?.members || []).filter(m => m.status === 'active');
        setTeamMembers(activeMembers);
      } catch (err) {
        console.error('Failed to load team members:', err);
      } finally {
        setLoadingTeam(false);
      }
    };
    
    if (isOpen) {
      loadTeam();
    }
  }, [isOpen]);
  
  // Load linked epics
  const loadLinkedEpics = useCallback(async () => {
    if (!initiative?.initiative_id || !isOpen) {
      console.log('[loadLinkedEpics] Skipped — initiative_id:', initiative?.initiative_id, 'isOpen:', isOpen);
      return;
    }
    
    console.log('[loadLinkedEpics] Fetching epics for initiative:', initiative.initiative_id);
    setLoadingEpics(true);
    try {
      const res = await epicsApi.listByInitiative(initiative.initiative_id);
      console.log('[loadLinkedEpics] Got', res.data?.length, 'epics:', res.data?.map(e => e.title));
      setLinkedEpics(res.data || []);
    } catch (err) {
      console.error('[loadLinkedEpics] Failed:', err);
      setLinkedEpics([]);
    } finally {
      setLoadingEpics(false);
    }
  }, [initiative?.initiative_id, isOpen]);
  
  useEffect(() => {
    loadLinkedEpics();
  }, [loadLinkedEpics]);
  
  // Extract productId from productContext
  const productId = productContext?.product_id;
  
  // Load available tags for the product
  const loadAvailableTags = useCallback(async () => {
    if (!productId || !isOpen) return;
    try {
      const res = await productTagsApi.list(productId);
      setAvailableTags(res.data.tags || []);
    } catch (err) {
      console.error('Failed to load tags:', err);
      setAvailableTags([]);
    }
  }, [productId, isOpen]);
  
  // Load initiative-owned tags
  const loadOwnedTags = useCallback(async () => {
    if (!initiative?.initiative_id || !isOpen) return;
    try {
      const res = await initiativesApi.getTags(initiative.initiative_id);
      setOwnedTags(res.data.tags || []);
    } catch (err) {
      console.error('Failed to load owned tags:', err);
      setOwnedTags([]);
    }
  }, [initiative?.initiative_id, isOpen]);
  
  useEffect(() => {
    loadAvailableTags();
  }, [loadAvailableTags]);
  
  useEffect(() => {
    loadOwnedTags();
  }, [loadOwnedTags]);
  
  // Load tagged features when in tags mode
  const loadTaggedFeatures = useCallback(async () => {
    if (!initiative?.initiative_id || formData.follow_type !== 'tags' || !isOpen) return;
    if (formData.tag_ids.length === 0) {
      setTaggedFeatures([]);
      return;
    }
    
    try {
      setLoadingTaggedFeatures(true);
      const res = await initiativesApi.getTaggedFeatures(initiative.initiative_id);
      setTaggedFeatures(res.data.features || []);
    } catch (err) {
      console.error('Failed to load tagged features:', err);
      setTaggedFeatures([]);
    } finally {
      setLoadingTaggedFeatures(false);
    }
  }, [initiative?.initiative_id, formData.follow_type, formData.tag_ids, isOpen]);
  
  useEffect(() => {
    loadTaggedFeatures();
  }, [loadTaggedFeatures]);

  // Register parent refresh callbacks on context so co-pilot-linked child modals
  // can refresh this modal's local state instead of triggering full page refresh.
  useEffect(() => {
    if (showAICopilot && isOpen) {
      registerParentCallbacks({
        epic: loadLinkedEpics,
        feature: loadTaggedFeatures,
        markDirty: (collection) => dirtyCollectionsRef.current.add(collection),
      });
    }
    return () => unregisterParentCallbacks();
  }, [showAICopilot, isOpen, registerParentCallbacks, unregisterParentCallbacks]); // eslint-disable-line react-hooks/exhaustive-deps

  
  // Helper: Get contrast color for tag text
  const getContrastColor = (hexColor) => {
    if (!hexColor) return '#ffffff';
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#1f2937' : '#ffffff';
  };
  
  // Handle adding a tag
  const addTag = (tag) => {
    if (!formData.tag_ids.includes(tag.tag_id)) {
      handleChange('tag_ids', [...formData.tag_ids, tag.tag_id]);
    }
    setTagSearch('');
    setShowTagDropdown(false);
  };
  
  // Handle removing a tag
  const removeTag = (tagId) => {
    handleChange('tag_ids', formData.tag_ids.filter(id => id !== tagId));
  };
  
  // Create a new initiative-owned tag
  const createOwnedTag = async () => {
    if (!newTagName.trim() || !initiative?.initiative_id) return;
    
    try {
      const res = await initiativesApi.createTag(initiative.initiative_id, {
        name: newTagName.trim(),
        color: newTagColor
      });
      
      if (res.data) {
        // Add to owned tags list
        setOwnedTags(prev => [...prev, res.data]);
        // Auto-add to tracked tags
        handleChange('tag_ids', [...formData.tag_ids, res.data.tag_id]);
        toast.success(`Created tag "${res.data.name}"`);
        setNewTagName('');
        setNewTagColor('#3B82F6');
        setShowCreateTagModal(false);
      }
    } catch (err) {
      console.error('Failed to create tag:', err);
      toast.error(err.response?.data?.detail || 'Failed to create tag');
    }
  };
  
  // Update an initiative-owned tag
  const updateOwnedTag = async (tagId, updates) => {
    if (!initiative?.initiative_id) return;
    
    try {
      const res = await initiativesApi.updateTag(initiative.initiative_id, tagId, updates);
      if (res.data) {
        setOwnedTags(prev => prev.map(t => t.tag_id === tagId ? res.data : t));
        toast.success('Tag updated');
        setEditingTag(null);
      }
    } catch (err) {
      console.error('Failed to update tag:', err);
      toast.error(err.response?.data?.detail || 'Failed to update tag');
    }
  };
  
  // Delete an initiative-owned tag
  const deleteOwnedTag = async (tagId, force = false) => {
    if (!initiative?.initiative_id) return;
    
    try {
      await initiativesApi.deleteTag(initiative.initiative_id, tagId, force);
      
      // Remove from owned tags list
      setOwnedTags(prev => prev.filter(t => t.tag_id !== tagId));
      // Remove from tracked tags if present
      if (formData.tag_ids.includes(tagId)) {
        handleChange('tag_ids', formData.tag_ids.filter(id => id !== tagId));
      }
      
      toast.success('Tag deleted');
      setDeletingTagId(null);
      
      // Refresh tagged features
      loadTaggedFeatures();
    } catch (err) {
      console.error('Failed to delete tag:', err);
      if (err.response?.status === 400 && !force) {
        // Tag is in use, ask for confirmation
        toast.error(err.response?.data?.detail || 'Tag is in use');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to delete tag');
      }
    }
  };
  
  // Check impact before switching follow type
  const checkFollowTypeImpact = async (targetType) => {
    if (!initiative?.initiative_id) return;
    
    try {
      const res = await initiativesApi.getFollowTypeImpact(initiative.initiative_id, targetType);
      if (res.data?.has_impact) {
        setFollowTypeSwitchImpact(res.data);
        setPendingFollowType(targetType);
        setShowFollowTypeSwitchConfirm(true);
      } else {
        // No impact, switch directly
        handleChange('follow_type', targetType);
      }
    } catch (err) {
      console.error('Failed to check follow type impact:', err);
      // Fallback to existing behavior
      handleFollowTypeChange(targetType);
    }
  };
  
  // Handle follow type change with confirmation
  const handleFollowTypeChange = (newType) => {
    const currentType = formData.follow_type;
    
    // Check if there are associations that would be lost
    if (currentType === 'epics' && newType === 'tags' && linkedEpics.length > 0) {
      setPendingFollowType(newType);
      setShowModeSwitchModal(true);
      return;
    }
    
    if (currentType === 'tags' && newType === 'epics' && formData.tag_ids.length > 0) {
      setPendingFollowType(newType);
      setShowModeSwitchModal(true);
      return;
    }
    
    // No associations, safe to switch
    handleChange('follow_type', newType);
  };
  
  // Confirm mode switch
  const confirmModeSwitch = async () => {
    if (!pendingFollowType) return;
    
    const currentType = formData.follow_type;
    
    if (currentType === 'epics' && pendingFollowType === 'tags') {
      // Unlink all epics
      for (const epic of linkedEpics) {
        try {
          await epicsApi.update(epic.epic_id, { initiative_id: null });
        } catch (err) {
          console.error('Failed to unlink epic:', err);
        }
      }
      setLinkedEpics([]);
      toast.success(`Unlinked ${linkedEpics.length} epic(s)`);
    } else if (currentType === 'tags' && pendingFollowType === 'epics') {
      // Clear all tags
      handleChange('tag_ids', []);
      setTaggedFeatures([]);
      toast.success('Cleared all tags');
    }
    
    handleChange('follow_type', pendingFollowType);
    setShowModeSwitchModal(false);
    setPendingFollowType(null);
  };
  
  // Cancel mode switch
  const cancelModeSwitch = () => {
    setShowModeSwitchModal(false);
    setPendingFollowType(null);
  };
  
  // Get unique epics from tagged features for filter dropdown
  const taggedFeatureEpics = React.useMemo(() => {
    const epicMap = {};
    taggedFeatures.forEach(f => {
      if (f.epic_id && !epicMap[f.epic_id]) {
        epicMap[f.epic_id] = f.epic_title || 'Unknown Epic';
      }
    });
    return Object.entries(epicMap).map(([id, title]) => ({ epic_id: id, title }));
  }, [taggedFeatures]);
  
  // Filter tagged features
  const filteredTaggedFeatures = React.useMemo(() => {
    return taggedFeatures.filter(f => {
      const matchesSearch = !featureSearchQuery || 
        f.title?.toLowerCase().includes(featureSearchQuery.toLowerCase()) ||
        f.description?.toLowerCase().includes(featureSearchQuery.toLowerCase());
      const matchesEpic = featureEpicFilter === 'all' || f.epic_id === featureEpicFilter;
      return matchesSearch && matchesEpic;
    });
  }, [taggedFeatures, featureSearchQuery, featureEpicFilter]);
  
  // Initialize form data when initiative changes
  // Track which initiative ID has been initialized — prevents re-initialization on prop reference changes
  const initializedForIdRef = useRef(null);

  // Initialize form data ONCE per modal open session (guarded by initiative ID)
  useEffect(() => {
    const currentId = initiative?.initiative_id;
    if (!currentId) return;
    // Only initialize if this is a new initiative (different ID) — not a prop reference change
    if (initializedForIdRef.current === currentId) return;
    initializedForIdRef.current = currentId;

    // Handle goal_ids with legacy goal_id fallback
    const goalIds = initiative.goal_ids || (initiative.goal_id ? [initiative.goal_id] : []);
    setFormData({
      title: initiative.title || '',
      description: initiative.description || '',
      status: initiative.status || 'draft',
      goal_ids: goalIds,
      target_date: initiative.target_date 
        ? new Date(initiative.target_date).toISOString().split('T')[0] 
        : '',
      date_confidence: initiative.date_confidence || 'medium',
      is_blocked: initiative.is_blocked || false,
      blocker_reason: initiative.blocker_reason || '',
      progress_mode: initiative.progress_mode || 'auto',
      manual_progress_percent: initiative.manual_progress_percent || 0,
      owner_id: initiative.owner_id || '',
      follow_type: initiative.follow_type || 'epics',
      tag_ids: initiative.tag_ids || [],
      subscribed_kpi_ids: initiative.subscribed_kpi_ids || [],
    });
    setOriginalFollowType(initiative.follow_type || 'epics');
    setIsEditMode(false);
    setHasChanges(false);
    setKpisModified(false);
  }, [initiative]);

  // Reset initialization guard when modal closes
  useEffect(() => {
    if (!isOpen) {
      initializedForIdRef.current = null;
    }
  }, [isOpen]);
  
  
  // Handle close - check for unsaved changes first
  const handleClose = () => {
    // First check for unsaved form changes
    if (hasChanges) {
      setShowUnsavedWarning(true);
      return;
    }
    // No longer need close guard - pending actions persist in chat log
    performClose();
  };
  
  // Actually perform the close action
  const performClose = async () => {
    // Flush dirty collections as stale so the product page refreshes them
    if (dirtyCollectionsRef.current.size > 0 && onSave) {
      // Signal parent that child entities changed — parent will markStale + refresh this entity
      onSave(null, { 
        dirtyCollections: Array.from(dirtyCollectionsRef.current),
        entityId: initiative?.initiative_id,
      });
    }
    dirtyCollectionsRef.current.clear();
    
    // Reset state
    setActiveTab('overview');
    setLinkedEpics([]);
    setShowDeleteConfirm(false);
    setShowAddEpicModal(false);
    setShowUnsavedWarning(false);
    
    onClose();
  };
  
  // Save and then close
  const handleSaveAndClose = async () => {
    const saveSuccess = await handleSave(false);
    if (saveSuccess) {
      setShowUnsavedWarning(false);
      performClose();
    }
  };
  
  // Save initiative - returns true on success, false on failure
  // Now also notifies parent for entity-level refresh (parent UI updates immediately)
  const handleSave = async (closeAfter = false) => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return false;
    }
    
    // Goals are now optional - can be linked later
    // but warn if creating without any goals
    if (mode === 'create' && (!formData.goal_ids || formData.goal_ids.length === 0)) {
      // Still allow creation, but with a hint
      console.log('Creating initiative without linked goals');
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        target_date: formData.target_date 
          ? new Date(formData.target_date).toISOString() 
          : null,
        blocker_reason: formData.is_blocked ? formData.blocker_reason : null,
        owner_id: formData.owner_id === 'none' ? null : formData.owner_id || null,
      };
      
      let savedData = null;
      
      if (mode === 'create') {
        // Use product-level create if no goals, or goal-level if goals are linked
        let response;
        if (formData.goal_ids && formData.goal_ids.length > 0) {
          response = await initiativesApi.create(formData.goal_ids[0], payload);
        } else if (productId) {
          response = await initiativesApi.createForProduct(productId, payload);
        } else {
          toast.error('Cannot create initiative without product context');
          setSaving(false);
          return false;
        }
        savedData = response?.data;
        setHasChanges(false);
        
        // Notify parent to refresh collection (new entity needs to be added to list)
        if (!standalone && onSave) {
          await onSave(savedData, { kpisChanged: kpisModified });
        }
        // Trigger cascade refresh if KPIs were modified
        if (kpisModified && onKpiChange) {
          onKpiChange();
        }
        setKpisModified(false);  // Reset after save
        toast.success('Initiative created!');
      } else {
        const updateResponse = await initiativesApi.update(initiative.initiative_id, payload);
        savedData = updateResponse?.data;
        setHasChanges(false);
        
        // Determine if follow_type changed (for cascade refresh)
        const followTypeChanged = formData.follow_type !== originalFollowType;
        const changeInfo = {
          kpisChanged: kpisModified,
          followTypeChanged,
          previousFollowType: followTypeChanged ? originalFollowType : null
        };
        
        // Notify parent — child mode uses parentRefresh, parent mode uses onSave
        if (!standalone) {
          if (parentRefresh?.initiative) {
            parentRefresh.initiative();
            parentRefresh.markDirty?.('initiatives');
            if (kpisModified) parentRefresh.markDirty?.('kpis');
          } else if (onSave) {
            await onSave(savedData, changeInfo);
          }
        }
        // Trigger cascade refresh if KPIs were modified
        if (kpisModified && onKpiChange) {
          onKpiChange();
        }
        
        // Update originalFollowType after successful save
        if (followTypeChanged) {
          setOriginalFollowType(formData.follow_type);
        }
        setKpisModified(false);  // Reset after save
        toast.success('Initiative updated!');
      }
      
      if (closeAfter) {
        // Call onSaveAndClose if provided for close-specific logic
        const followTypeChanged = formData.follow_type !== originalFollowType;
        if (!standalone && onSaveAndClose) {
          await onSaveAndClose(savedData, { 
            kpisChanged: kpisModified,
            followTypeChanged,
            previousFollowType: followTypeChanged ? originalFollowType : null
          });
        }
        performClose();
      }
      return true;
    } catch (err) {
      console.error('Error saving initiative:', err);
      toast.error(err.response?.data?.detail || 'Failed to save initiative');
      return false;
    } finally {
      setSaving(false);
    }
  };
  
  // Delete initiative (via modal)
  const handleDelete = async () => {
    if (!initiative?.initiative_id) return;
    
    setDeleting(true);
    try {
      await initiativesApi.delete(initiative.initiative_id);
      toast.success('Initiative deleted');
      setShowDeleteConfirm(false);
      
      // STANDALONE MODE: Skip parent callback
      if (!standalone) {
        onDelete?.();
      }
      onClose();
    } catch (err) {
      console.error('Error deleting initiative:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete initiative');
    } finally {
      setDeleting(false);
    }
  };
  
  // Handle opening an epic modal (opens OVER Initiative modal)
  const handleOpenEpic = (epic) => {
    if (suppressChildModals) return;
    setSelectedEpicForModal(epic);
  };
  
  // Handle epic modal close
  const handleCloseEpicModal = () => {
    setSelectedEpicForModal(null);
  };
  
  // Handle epic linked
  const handleEpicLinked = () => {
    loadLinkedEpics();
    
    // STANDALONE MODE: Skip parent callback
    if (!standalone && onSave) {
      onSave(); // Refresh parent data
    }
  };
  
  // Handle epic unlinked
  const handleUnlinkEpic = async (epicId, epicTitle) => {
    try {
      await epicsApi.unlinkFromInitiative(epicId);
      toast.success(`"${epicTitle}" unlinked from initiative`);
      loadLinkedEpics(); // Refresh the list
      if (onSave) onSave(); // Refresh parent data
    } catch (err) {
      console.error('Failed to unlink epic:', err);
      toast.error('Failed to unlink epic');
    }
  };
  
  // Compute progress percentage
  const computedProgress = formData.progress_mode === 'manual' 
    ? formData.manual_progress_percent 
    : (initiative?.computed_progress || 0);
  
  if (!isOpen) return null;
  
  return (
    <ModalZIndexProvider zIndex={zIndex}>
    <>
      <div className="fixed inset-0" style={{ zIndex }}>
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/60" onClick={handleClose} />
        
        {/* Modal - Full screen like Feature modal */}
        <div className={cn(
          "fixed inset-4 md:inset-8 lg:inset-12 rounded-xl shadow-2xl flex flex-col overflow-hidden",
          colors.bgCard
        )}>
          {/* Header */}
          <div className={cn("flex items-center justify-between px-6 py-4 border-b", colors.borderPrimary)}>
            <div className="flex items-center gap-3">
              <div>
                <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>
                  {mode === 'create' ? 'Create Initiative' : (isEditing ? 'Edit Initiative' : 'View Initiative')}
                </h2>
                {mode !== 'create' && formData.title && (
                  <p className={cn("text-sm truncate max-w-[400px]", colors.textMuted)}>{formData.title}</p>
                )}
              </div>
              {/* Edit Mode Toggle */}
              {showEditToggle && (
                <div className="flex items-center gap-2">
                  <span className={cn("text-xs", colors.textMuted)}>View</span>
                  <button
                    onClick={() => setIsEditMode(!isEditMode)}
                    className={cn(
                      "relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                      isEditMode ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"
                    )}
                    role="switch"
                    aria-checked={isEditMode}
                    data-testid="edit-mode-toggle"
                  >
                    <span
                      className={cn(
                        "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out",
                        isEditMode ? "translate-x-5" : "translate-x-0"
                      )}
                    />
                  </button>
                  <span className={cn("text-xs", isEditMode ? "text-blue-600 dark:text-blue-400 font-medium" : colors.textMuted)}>Edit</span>
                </div>
              )}
            </div>
            
            {/* Header Action Buttons */}
            <div className="flex items-center gap-2">
              {mode === 'edit' && isEditing && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                  title="Delete initiative"
                  data-testid="delete-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => handleSave(false)}
                disabled={saving || !hasChanges || targetDateError}
                variant={hasChanges ? "default" : "outline"}
                className={cn(hasChanges && !targetDateError ? colors.accentGradient : "opacity-50 cursor-not-allowed")}
                data-testid="save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span className="ml-1">Save</span>
              </Button>
              <Button
                size="sm"
                onClick={() => handleSave(true)}
                disabled={saving || !hasChanges || targetDateError}
                variant={hasChanges ? "default" : "outline"}
                className={cn(hasChanges && !targetDateError ? colors.accentGradient : "opacity-50 cursor-not-allowed")}
                data-testid="save-close-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span className="ml-1">Save & Close</span>
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClose}
                className={cn(colors.textMuted, "hover:bg-gray-200 dark:hover:bg-gray-700")}
                data-testid="close-btn"
              >
                Close
              </Button>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left: Form Fields (60%) */}
            <div className={cn(
              "overflow-y-auto flex flex-col",
              showAICopilot ? "w-[60%] border-r" : "w-full",
              colors.borderPrimary
            )}>
              {/* Tab Navigation - Pill Style at TOP */}
              <div className="px-6 pt-4">
                <Tabs value={activeTab} onValueChange={(tab) => {
                  setActiveTab(tab);
                }} className="w-full">
                  <TabsList className={cn("mb-4 border inline-flex", colors.bgCard, colors.borderPrimary)}>
                    <TabsTrigger 
                      value="overview" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                      data-testid="tab-overview"
                    >
                      Overview
                    </TabsTrigger>
                    <TabsTrigger 
                      value="goals" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                      data-testid="tab-goals"
                    >
                      <Target className="w-3 h-3 mr-1" />
                      Goals {formData.goal_ids?.length > 0 && `(${formData.goal_ids.length})`}
                    </TabsTrigger>
                    <TabsTrigger 
                      value="targets" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                      data-testid="tab-targets"
                    >
                      <Target className="w-3 h-3 mr-1" />
                      KPIs {(formData.subscribed_kpi_ids?.length > 0) && `(${formData.subscribed_kpi_ids.length})`}
                    </TabsTrigger>
                    {formData.follow_type === 'epics' && (
                      <TabsTrigger 
                        value="epics" 
                        className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                        data-testid="tab-epics"
                      >
                        Epics {linkedEpics.length > 0 && `(${linkedEpics.length})`}
                      </TabsTrigger>
                    )}
                    {formData.follow_type === 'tags' && (
                      <TabsTrigger 
                        value="tags" 
                        className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                        data-testid="tab-tags"
                      >
                        Tags {formData.tag_ids.length > 0 && `(${formData.tag_ids.length})`}
                      </TabsTrigger>
                    )}
                    <TabsTrigger 
                      value="progress" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                      data-testid="tab-progress"
                    >
                      Progress
                    </TabsTrigger>
                    <TabsTrigger 
                      value="attachments" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                      data-testid="tab-attachments"
                    >
                      <Paperclip className="w-3.5 h-3.5 mr-1" />
                      Attachments
                    </TabsTrigger>
                    <TabsTrigger 
                      value="notes" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                      data-testid="tab-notes"
                    >
                      <MessageSquare className="w-3.5 h-3.5 mr-1" />
                      Notes
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              
              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                
                {/* ===== OVERVIEW TAB ===== */}
                {activeTab === 'overview' && (
                  <>
                    {/* Key Attributes Section */}
                    <div className="space-y-4">
                      {/* Row 1: Title | Owner */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col">
                          <Label className={colors.textSecondary}>Title *</Label>
                          <Input
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            placeholder="Mobile App Launch"
                            disabled={!isEditing}
                            className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary, !isEditing && "opacity-70 cursor-not-allowed")}
                            data-testid="title-input"
                          />
                        </div>
                        <div className="flex flex-col">
                          <Label className={colors.textSecondary}>
                            Owner {formData.status === 'draft' && '*'}
                          </Label>
                          <Select 
                            value={formData.owner_id || 'none'} 
                            onValueChange={(value) => handleChange('owner_id', value === 'none' ? '' : value)}
                            disabled={!isEditing}
                          >
                            <SelectTrigger 
                              className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary, !isEditing && "opacity-70 cursor-not-allowed")}
                              data-testid="owner-select"
                            >
                              <SelectValue placeholder={loadingTeam ? "Loading..." : "Unassigned"} />
                            </SelectTrigger>
                            <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                              <SelectItem value="none">Unassigned</SelectItem>
                              {teamMembers.map(member => (
                                <SelectItem key={member.user_id} value={member.user_id}>
                                  {member.name || member.email}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Row 2: Status | Progress Follows | Target Date */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="flex flex-col">
                          <Label className={colors.textSecondary}>Status</Label>
                          <Select 
                            value={formData.status} 
                            onValueChange={(value) => handleChange('status', value)}
                          >
                            <SelectTrigger 
                              className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                              data-testid="status-select"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                              {STATUS_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  <div className="flex items-center gap-2">
                                    <div className={cn("w-2 h-2 rounded-full", opt.color)} />
                                    {opt.label}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Progress Follows */}
                        <div>
                          <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
                            <BarChart3 className="w-3 h-3" /> Progress Follows
                          </Label>
                          <Select 
                            value={formData.follow_type} 
                            onValueChange={handleFollowTypeChange}
                            disabled={!isEditing}
                          >
                            <SelectTrigger 
                              className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary, !isEditing && "opacity-70 cursor-not-allowed")}
                              data-testid="follow-type-select"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                              <SelectItem value="epics">
                                <span className="flex items-center gap-2">
                                  <Rocket className="w-3.5 h-3.5" /> Follows Epics
                                </span>
                              </SelectItem>
                              <SelectItem value="tags">
                                <span className="flex items-center gap-2">
                                  <Tag className="w-3.5 h-3.5" /> Follows Tags
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Target Date */}
                        <div>
                          <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
                            <Calendar className="w-3 h-3" /> Target Date {formData.status === 'draft' && '*'}
                          </Label>
                          <div className="mt-1">
                            <DatePickerWithMarkers
                              value={formData.target_date || ''}
                              onChange={(date) => handleChange('target_date', date)}
                              parentDeadlines={parentDeadlines}
                              placeholder="Select target date"
                              disabled={!isEditing}
                            />
                          </div>
                          {targetDateError && (
                            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              {targetDateError}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <Label className={colors.textSecondary}>Description</Label>
                      {!isEditing ? (
                        <div className={cn("mt-1 p-3 rounded-lg", colors.bgTertiary)}>
                          {formData.description ? (
                            <div className={cn("prose prose-sm max-w-none dark:prose-invert", colors.textPrimary)}>
                              <ReactMarkdown>{formData.description}</ReactMarkdown>
                            </div>
                          ) : (
                            <p className={cn("text-sm", colors.textMuted)}>No description provided</p>
                          )}
                        </div>
                      ) : (
                        <div className="mt-1" data-testid="description-textarea">
                          <MarkdownEditor
                            content={formData.description || ''}
                            onChange={(value) => handleChange('description', value)}
                            placeholder="Launch iOS and Android apps by Q2..."
                            minHeight={250}
                            disabled={!isEditing}
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Blockers Section */}
                    <div className={cn(
                      "p-4 rounded-lg border transition-all",
                      formData.is_blocked 
                        ? "border-red-500 bg-red-50 dark:bg-red-900/20" 
                        : cn(colors.bgTertiary, colors.borderPrimary)
                    )}>
                      <div className="flex items-center justify-between mb-3">
                        <Label className={cn("flex items-center gap-2 text-sm font-medium", formData.is_blocked ? "text-red-700 dark:text-red-300" : colors.textPrimary)}>
                          <AlertTriangle className="w-4 h-4" />
                          Blockers
                        </Label>
                        
                        <div className="flex items-center gap-2">
                          <span className={cn("text-sm", formData.is_blocked ? "text-red-700 dark:text-red-300" : colors.textSecondary)}>
                            Is Blocked?
                          </span>
                          <Switch
                            checked={formData.is_blocked}
                            onCheckedChange={(checked) => handleChange('is_blocked', checked)}
                            disabled={!isEditing}
                            data-testid="is-blocked-switch"
                          />
                        </div>
                      </div>
                      
                      {formData.is_blocked && (
                        <div className="space-y-2">
                          <Label className="text-red-700 dark:text-red-300 text-sm">Blocker Reason</Label>
                          <Textarea
                            value={formData.blocker_reason}
                            onChange={(e) => handleChange('blocker_reason', e.target.value)}
                            placeholder="e.g., Waiting on API Keys from Partner"
                            rows={2}
                            disabled={!isEditing}
                            className="border-red-300 dark:border-red-700"
                            data-testid="blocker-reason-textarea"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                {/* ===== GOALS TAB (Multi-Goal Linking) ===== */}
                {activeTab === 'goals' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className={cn("font-medium flex items-center gap-2", colors.textPrimary)}>
                          <Target className="w-4 h-4 text-indigo-500" />
                          Linked Goals
                        </h3>
                        <p className={cn("text-sm mt-0.5", colors.textMuted)}>
                          Link this initiative to one or more Goals to inherit their metrics
                        </p>
                      </div>
                    </div>
                    
                    {/* Add Goal Selector - NOW ABOVE LINKED GOALS */}
                    {isEditing && (
                      <div className="relative" ref={goalDropdownRef}>
                        <Label className={cn("mb-1 block", colors.textSecondary)}>
                          Add Goal
                        </Label>
                        <div className="relative">
                          <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
                          <Input
                            type="text"
                            placeholder="Search and select a goal to link..."
                            value={goalSearch}
                            onChange={(e) => {
                              setGoalSearch(e.target.value);
                              setShowGoalDropdown(true);
                            }}
                            onFocus={() => setShowGoalDropdown(true)}
                            className={cn("pl-10", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                            data-testid="goal-search-input"
                          />
                        </div>
                        
                        {/* Goal Dropdown */}
                        {showGoalDropdown && (
                          <div className={cn(
                            "absolute z-50 w-full mt-1 max-h-60 overflow-y-auto rounded-lg border shadow-lg",
                            colors.bgCard, colors.borderPrimary
                          )}>
                            {goals
                              .filter(g => 
                                !formData.goal_ids?.includes(g.goal_id) &&
                                (!goalSearch || g.title.toLowerCase().includes(goalSearch.toLowerCase()))
                              )
                              .map(goal => (
                                <button
                                  key={goal.goal_id}
                                  onClick={() => {
                                    handleChange('goal_ids', [...(formData.goal_ids || []), goal.goal_id]);
                                    setGoalSearch('');
                                    setShowGoalDropdown(false);
                                  }}
                                  className={cn(
                                    "w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                                    colors.textPrimary
                                  )}
                                >
                                  <Target className="w-4 h-4 text-indigo-500" />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{goal.title}</p>
                                    <p className={cn("text-xs", colors.textMuted)}>
                                      {goal.kpis?.length || 0} metrics
                                    </p>
                                  </div>
                                  <Plus className={cn("w-4 h-4", colors.textMuted)} />
                                </button>
                              ))
                            }
                            {goals.filter(g => !formData.goal_ids?.includes(g.goal_id)).length === 0 && (
                              <div className={cn("p-4 text-center", colors.textMuted)}>
                                <p className="text-sm">All available goals are already linked</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Current Linked Goals */}
                    {formData.goal_ids?.length > 0 ? (
                      <div className="space-y-2">
                        {formData.goal_ids.map(gid => {
                          const goal = goals.find(g => g.goal_id === gid);
                          // Show goal even if not found in goals prop (may be from different context)
                          return (
                            <div 
                              key={gid}
                              className={cn(
                                "flex items-center justify-between p-3 rounded-lg border",
                                colors.bgTertiary, colors.borderPrimary
                              )}
                            >
                              <div className="flex items-center gap-3">
                                <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", "bg-indigo-100 dark:bg-indigo-900/30")}>
                                  <Target className="w-4 h-4 text-indigo-500" />
                                </div>
                                <div>
                                  <p className={cn("font-medium", colors.textPrimary)}>
                                    {goal?.title || 'Linked Goal'}
                                  </p>
                                  <p className={cn("text-xs", colors.textMuted)}>
                                    {goal ? `${goal.kpis?.length || 0} metrics available` : gid}
                                  </p>
                                </div>
                              </div>
                              {isEditing && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    const newGoalIds = formData.goal_ids.filter(id => id !== gid);
                                    handleChange('goal_ids', newGoalIds);
                                  }}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className={cn("p-6 rounded-lg border text-center", colors.bgTertiary, colors.borderPrimary)}>
                        <Target className={cn("w-10 h-10 mx-auto mb-2 opacity-40", colors.textMuted)} />
                        <p className={cn("font-medium", colors.textSecondary)}>No goals linked yet</p>
                        <p className={cn("text-sm mt-1", colors.textMuted)}>
                          Link at least one Goal to set metric targets and track outcomes
                        </p>
                      </div>
                    )}
                    
                    {/* Info Box */}
                    {formData.goal_ids?.length > 0 && (
                      <div className={cn("p-3 rounded-lg border", "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800")}>
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          <strong>💡 What happens next?</strong> Go to the Targets tab to select which metrics 
                          from these Goals this Initiative will commit to moving.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* ===== KPI SUBSCRIPTIONS TAB (Outcome-Driven Model) ===== */}
                {activeTab === 'targets' && (
                  <KpiSubscriptionTab
                    entityType="initiative"
                    entityId={initiative?.initiative_id}
                    disabled={!isEditing}
                    fetchInheritedKpis={async (id) => {
                      // Pass current form goal_ids to get KPIs based on unsaved selections
                      const goalIdsParam = formData.goal_ids?.length 
                        ? `?goal_ids=${formData.goal_ids.join(',')}` 
                        : '';
                      const response = await initiativesApi.getInheritedKpis(id, goalIdsParam);
                      return response.data;
                    }}
                    currentSubscriptions={formData.subscribed_kpi_ids || []}
                    linkedGoalIds={formData.goal_ids || []}
                    onChange={(subscriptions) => {
                      setFormData(prev => ({
                        ...prev,
                        subscribed_kpi_ids: subscriptions
                      }));
                      setHasChanges(true);
                      setKpisModified(true);  // Track KPI change for cascade
                    }}
                    subscriptionFieldName="subscribed_kpi_ids"
                  />
                )}
                
                {/* ===== PROGRESS TAB ===== */}
                {activeTab === 'progress' && (
                  <>
                    {/* Progress Section with Toggle */}
                    <div className="space-y-4">
                      {/* Progress Mode Toggle */}
                      <div className="flex items-center justify-between">
                        <Label className={cn("flex items-center gap-2", colors.textPrimary)}>
                          <BarChart3 className="w-4 h-4" />
                          Progress
                        </Label>
                        <div className="flex items-center gap-2">
                          <span className={cn("text-sm", formData.progress_mode === 'auto' ? colors.textAccent : colors.textMuted)}>
                            Auto
                          </span>
                          <Switch
                            checked={formData.progress_mode === 'manual'}
                            onCheckedChange={(checked) => handleChange('progress_mode', checked ? 'manual' : 'auto')}
                            disabled={!isEditing}
                          />
                          <span className={cn("text-sm", formData.progress_mode === 'manual' ? colors.textAccent : colors.textMuted)}>
                            Manual
                          </span>
                        </div>
                      </div>
                      
                      {/* Auto Progress Bar OR Manual Slider */}
                      {formData.progress_mode === 'auto' ? (
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className={cn("text-sm", colors.textMuted)}>
                              Calculated from {formData.follow_type === 'tags' ? 'tagged features' : 'features in linked epics'}
                            </span>
                            <span className={cn("text-lg font-bold", colors.textAccent)}>
                              {computedProgress}%
                            </span>
                          </div>
                          <div className={cn("w-full h-3 rounded-full overflow-hidden", colors.bgTertiary)}>
                            <div 
                              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300"
                              style={{ width: `${computedProgress}%` }}
                            />
                          </div>
                          {initiative?.feature_stats && (
                            <p className={cn("text-xs", colors.textMuted)}>
                              {initiative.feature_stats.done} / {initiative.feature_stats.total} features completed
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className={cn("text-sm", colors.textMuted)}>Manual override</span>
                            <span className={cn("text-lg font-bold", colors.textAccent)}>
                              {formData.manual_progress_percent}%
                            </span>
                          </div>
                          <Slider
                            value={[formData.manual_progress_percent]}
                            onValueChange={([value]) => handleChange('manual_progress_percent', value)}
                            max={100}
                            step={5}
                            disabled={!isEditing}
                            className="py-2"
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Schedule Section */}
                    <div className={cn("p-4 rounded-lg border mt-6", colors.bgTertiary, colors.borderPrimary)}>
                      <h4 className={cn("text-sm font-semibold mb-4 flex items-center gap-2", colors.textPrimary)}>
                        <Clock className="w-4 h-4" />
                        Schedule
                      </h4>
                      
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <Label className={colors.textSecondary}>Target Date</Label>
                          <p className={cn("mt-1 text-sm", colors.textPrimary)}>
                            {formData.target_date 
                              ? new Date(formData.target_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                              : 'Not set'}
                          </p>
                        </div>
                        <div className="flex-1">
                          <Label className={colors.textSecondary}>Date Confidence</Label>
                          <p className={cn("mt-1 text-sm", CONFIDENCE_OPTIONS.find(o => o.value === formData.date_confidence)?.color || colors.textPrimary)}>
                            {CONFIDENCE_OPTIONS.find(o => o.value === formData.date_confidence)?.label || formData.date_confidence}
                          </p>
                        </div>
                      </div>
                      
                      {formData.is_blocked && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-3">
                          ⚠️ Date confidence is set to Low because this initiative is blocked
                        </p>
                      )}
                    </div>
                  </>
                )}
                
                {/* ===== EPICS TAB ===== */}
                {activeTab === 'epics' && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <Label className={cn("flex items-center gap-2", colors.textPrimary)}>
                        <Rocket className="w-4 h-4" />
                        Linked Epics ({linkedEpics.length})
                      </Label>
                      {isEditing && mode === 'edit' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className={cn("gap-1", colors.borderPrimary)}
                          onClick={() => setShowAddEpicModal(true)}
                          data-testid="add-epic-btn"
                        >
                          <Plus className="w-3 h-3" />
                          Add Epic
                        </Button>
                      )}
                    </div>
                    
                    {loadingEpics ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
                      </div>
                    ) : linkedEpics.length === 0 ? (
                      <div className={cn("text-center py-8 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                        <Rocket className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
                        <p className={cn("text-sm", colors.textMuted)}>No epics linked to this initiative</p>
                        {mode === 'create' && (
                          <p className={cn("text-xs mt-1", colors.textMuted)}>
                            Save the initiative first to link epics
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {linkedEpics.map(epic => (
                          <div 
                            key={epic.epic_id}
                            className={cn(
                              "flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors",
                              colors.bgTertiary, colors.borderPrimary
                            )}
                          >
                            <div 
                              className={cn("flex-1 min-w-0", !suppressChildModals && "cursor-pointer")}
                              onClick={() => handleOpenEpic(epic)}
                            >
                              <div className="flex items-center gap-2">
                                <span className={cn("font-medium truncate", colors.textPrimary)}>
                                  {epic.title}
                                </span>
                                <span className={cn(
                                  "text-xs px-2 py-0.5 rounded-full",
                                  epic.status === 'Done' ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" :
                                  epic.status === 'In Progress' ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" :
                                  "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                                )}>
                                  {epic.status}
                                </span>
                              </div>
                              {epic.description && (
                                <p className={cn("text-sm truncate mt-0.5", colors.textMuted)}>
                                  {epic.description}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                              {!suppressChildModals && (
                              <button
                                onClick={() => handleOpenEpic(epic)}
                                className={cn("p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700", colors.textMuted)}
                                title="Open Epic"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </button>
                              )}
                              {isEditing && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnlinkEpic(epic.epic_id, epic.title);
                                  }}
                                  className="p-1 rounded text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30"
                                  title="Unlink Epic"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
                
                {/* ===== TAGS TAB ===== */}
                {activeTab === 'tags' && (
                  <>
                    {/* Owned Tags Section - Full CRUD */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <Label className={cn("flex items-center gap-2", colors.textPrimary)}>
                          <Tag className="w-4 h-4" />
                          Initiative Tags ({ownedTags.length})
                        </Label>
                        {isEditing && (
                          <Button
                            onClick={() => setShowCreateTagModal(true)}
                            size="sm"
                            variant="outline"
                            className="text-xs"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Create Tag
                          </Button>
                        )}
                      </div>
                      
                      {ownedTags.length === 0 ? (
                        <div className={cn("text-center py-6 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                          <Tag className={cn("w-6 h-6 mx-auto mb-2 opacity-50", colors.textMuted)} />
                          <p className={cn("text-sm", colors.textMuted)}>No tags created yet</p>
                          <p className={cn("text-xs mt-1", colors.textMuted)}>
                            Create tags to track features in this initiative
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {ownedTags.map(tag => (
                            <div
                              key={tag.tag_id}
                              className={cn(
                                "flex items-center justify-between p-3 rounded-lg border",
                                formData.tag_ids.includes(tag.tag_id)
                                  ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                                  : cn(colors.bgTertiary, colors.borderPrimary)
                              )}
                            >
                              <div className="flex items-center gap-3">
                                {editingTag?.tag_id === tag.tag_id ? (
                                  <>
                                    <input
                                      type="color"
                                      value={editingTag.color || tag.color}
                                      onChange={(e) => setEditingTag(prev => ({ ...prev, color: e.target.value }))}
                                      className="w-6 h-6 rounded cursor-pointer"
                                    />
                                    <Input
                                      value={editingTag.name ?? tag.name}
                                      onChange={(e) => setEditingTag(prev => ({ ...prev, name: e.target.value }))}
                                      className={cn("text-sm w-32", colors.bgInput, colors.borderPrimary)}
                                      autoFocus
                                    />
                                  </>
                                ) : (
                                  <>
                                    <span
                                      className="w-4 h-4 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: tag.color || '#6b7280' }}
                                    />
                                    <span className={cn("text-sm font-medium", colors.textPrimary)}>
                                      {tag.name}
                                    </span>
                                    <span className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgSecondary, colors.textMuted)}>
                                      {tag.usage_count || 0} features
                                    </span>
                                  </>
                                )}
                              </div>
                              
                              <div className="flex items-center gap-1">
                                {/* Track/Untrack toggle */}
                                {!editingTag && (
                                  <button
                                    onClick={() => {
                                      if (formData.tag_ids.includes(tag.tag_id)) {
                                        removeTag(tag.tag_id);
                                      } else {
                                        addTag(tag);
                                      }
                                    }}
                                    className={cn(
                                      "p-1.5 rounded text-xs",
                                      formData.tag_ids.includes(tag.tag_id)
                                        ? "text-blue-600 dark:text-blue-400"
                                        : cn(colors.textMuted, "hover:text-blue-500")
                                    )}
                                    title={formData.tag_ids.includes(tag.tag_id) ? "Stop tracking" : "Start tracking"}
                                  >
                                    {formData.tag_ids.includes(tag.tag_id) ? (
                                      <Check className="w-4 h-4" />
                                    ) : (
                                      <Plus className="w-4 h-4" />
                                    )}
                                  </button>
                                )}
                                
                                {isEditing && (
                                  <>
                                    {editingTag?.tag_id === tag.tag_id ? (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => {
                                            updateOwnedTag(tag.tag_id, {
                                              name: editingTag.name,
                                              color: editingTag.color
                                            });
                                          }}
                                          className="h-7 px-2"
                                        >
                                          <Check className="w-3 h-3" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => setEditingTag(null)}
                                          className="h-7 px-2"
                                        >
                                          <X className="w-3 h-3" />
                                        </Button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          onClick={() => setEditingTag({ ...tag })}
                                          className={cn("p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700", colors.textMuted)}
                                          title="Edit tag"
                                        >
                                          <Pencil className="w-3 h-3" />
                                        </button>
                                        <button
                                          onClick={() => {
                                            if (tag.usage_count > 0) {
                                              setDeletingTagId(tag.tag_id);
                                            } else {
                                              deleteOwnedTag(tag.tag_id);
                                            }
                                          }}
                                          className="p-1.5 rounded text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30"
                                          title="Delete tag"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Feature Stats & Filters */}
                    {formData.tag_ids.length > 0 && (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <Label className={cn("flex items-center gap-2", colors.textPrimary)}>
                            <FileText className="w-4 h-4" />
                            Matched Features ({taggedFeatures.length})
                          </Label>
                        </div>
                        
                        <div className="flex items-center gap-3 mb-4">
                          <div className="relative flex-1">
                            <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
                            <Input
                              value={featureSearchQuery}
                              onChange={(e) => setFeatureSearchQuery(e.target.value)}
                              placeholder="Search features..."
                              className={cn("pl-9 text-sm", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                            />
                          </div>
                          <Select value={featureEpicFilter} onValueChange={setFeatureEpicFilter}>
                            <SelectTrigger className={cn("w-48", colors.bgInput, colors.borderPrimary, colors.textPrimary)}>
                              <Filter className="w-4 h-4 mr-2" />
                              <SelectValue placeholder="All Epics" />
                            </SelectTrigger>
                            <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                              <SelectItem value="all">All Epics</SelectItem>
                              {taggedFeatureEpics.map(epic => (
                                <SelectItem key={epic.epic_id} value={epic.epic_id}>
                                  {epic.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                    
                    {/* Tagged Features List - only show when there are tags to track */}
                    {loadingTaggedFeatures ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
                        <span className={cn("ml-2 text-sm", colors.textMuted)}>Loading features...</span>
                      </div>
                    ) : formData.tag_ids.length === 0 && ownedTags.length > 0 ? (
                      // Only show "track tags" message when there ARE owned tags but none are tracked
                      <div className={cn("text-center py-8 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                        <Tag className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
                        <p className={cn("text-sm", colors.textMuted)}>Track tags above to see matched features</p>
                        <p className={cn("text-xs mt-1", colors.textMuted)}>
                          Progress will be calculated from features with tracked tags
                        </p>
                      </div>
                    ) : formData.tag_ids.length > 0 && filteredTaggedFeatures.length === 0 ? (
                      <div className={cn("text-center py-8 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                        <FileText className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
                        <p className={cn("text-sm", colors.textMuted)}>
                          {featureSearchQuery || featureEpicFilter !== 'all' 
                            ? 'No features match your filters'
                            : 'No features found with the tracked tags'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {filteredTaggedFeatures.map(feature => (
                          <div 
                            key={feature.feature_id}
                            onClick={() => !suppressChildModals && setSelectedFeatureForModal(feature)}
                            className={cn(
                              "flex items-center justify-between p-3 rounded-lg border transition-colors",
                              !suppressChildModals && "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50",
                              colors.bgTertiary, colors.borderPrimary
                            )}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={cn("font-medium truncate", colors.textPrimary)}>
                                  {feature.title}
                                </span>
                                <span className={cn(
                                  "text-xs px-2 py-0.5 rounded-full flex-shrink-0",
                                  feature.status?.toLowerCase() === 'done' ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" :
                                  feature.status?.toLowerCase() === 'in progress' ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" :
                                  "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                                )}>
                                  {feature.status || 'Backlog'}
                                </span>
                              </div>
                              <p className={cn("text-xs truncate mt-0.5", colors.textMuted)}>
                                {feature.epic_title || 'Unknown Epic'}
                              </p>
                            </div>
                            {!suppressChildModals && <ExternalLink className={cn("w-4 h-4 flex-shrink-0 ml-2", colors.textMuted)} />}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
                
                {/* ===== ATTACHMENTS TAB ===== */}
                {activeTab === 'attachments' && initiative?.initiative_id && (
                  <AttachmentsPanel
                    entityType="initiative"
                    entityId={initiative.initiative_id}
                    productId={productContext?.product_id}
                    canEdit={true}
                    colors={colors}
                  />
                )}
                
                {/* ===== NOTES TAB ===== */}
                {activeTab === 'notes' && initiative?.initiative_id && (
                  <NotesPanel
                    entityType="initiative"
                    entityId={initiative.initiative_id}
                    teamMembers={teamMembers}
                    colors={colors}
                  />
                )}
              </div>
            </div>
            
            {/* Right: AI Co-Pilot (40%) - Only shown for existing initiatives */}
            {showAICopilot && (
              <div className={cn("w-[40%] flex flex-col", colors.bgTertiary)}>
                <UnifiedCoPilot
                  contextType="initiative"
                  contextId={initiative?.initiative_id}
                  productId={productContext?.product_id}
                  contextData={formData}
                  relatedData={{
                    product: productContext,
                    goals: goals,
                    children: linkedEpics,
                    parent: goals.find(g => g.goal_id === formData.goal_id)
                  }}
                  onApplyUpdates={handleApplyUpdates}
                  canEditField={() => true}
                  onRefresh={refreshInitiativeData}
                  variant="item"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Delete Confirmation Modal - z-index 60 to be above main modal */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={formData.title}
        isDeleting={deleting}
      />
      
      {/* Add Epic Modal - z-index 60 to be above main modal */}
      <AddEpicModal
        isOpen={showAddEpicModal}
        onClose={() => setShowAddEpicModal(false)}
        onSave={handleEpicLinked}
        productId={productContext.product_id}
        initiativeId={initiative?.initiative_id}
        existingEpicIds={linkedEpics.map(e => e.epic_id)}
      />
      
      {/* Epic Detail Modal - above Initiative modal */}
      {selectedEpicForModal && (
        <div className="fixed inset-0" style={{ zIndex: zIndex + 20 }}>
          <EpicDetailModal
            isOpen={!!selectedEpicForModal}
            onClose={handleCloseEpicModal}
            epic={selectedEpicForModal}
            productId={productContext.product_id}
            productContext={productContext}
            personas={personas}
            zIndex={zIndex + 20}
            suppressChildModals={true}
            parentRefresh={{
              epic: loadLinkedEpics,
              feature: loadTaggedFeatures,
              markDirty: (collection) => dirtyCollectionsRef.current.add(collection),
            }}
          />
        </div>
      )}
      
      {/* Feature Detail Modal - above Initiative modal */}
      {selectedFeatureForModal && (
        <div className="fixed inset-0" style={{ zIndex: zIndex + 20 }}>
          <FeatureDetailModal
            isOpen={!!selectedFeatureForModal}
            feature={selectedFeatureForModal}
            onClose={() => setSelectedFeatureForModal(null)}
            personas={personas}
            goals={goals}
            teamMembers={teamMembers}
            productContext={productContext}
            zIndex={zIndex + 20}
            suppressChildModals={true}
            parentRefresh={{
              epic: loadLinkedEpics,
              feature: loadTaggedFeatures,
              markDirty: (collection) => dirtyCollectionsRef.current.add(collection),
            }}
          />
        </div>
      )}
      
      {/* Mode Switch Confirmation Modal */}
      {showModeSwitchModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50">
          <div className={cn("w-full max-w-md mx-4 rounded-lg shadow-xl", colors.bgCard)}>
            <div className={cn("p-6 border-b", colors.borderPrimary)}>
              <h3 className={cn("text-lg font-semibold flex items-center gap-2", colors.textPrimary)}>
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Switch Progress Tracking Mode
              </h3>
            </div>
            <div className="p-6">
              {pendingFollowType === 'tags' ? (
                <>
                  <p className={cn("text-sm font-medium mb-3", colors.textPrimary)}>
                    Switching to Tags mode will:
                  </p>
                  <ul className={cn("text-sm mb-4 space-y-1", colors.textSecondary)}>
                    <li>• Unlink all <strong>{linkedEpics.length}</strong> epic(s)</li>
                    <li>• Unlink all associated features</li>
                  </ul>
                  <p className={cn("text-sm", colors.textSecondary)}>
                    The epics will remain in your product but won&apos;t be associated with this initiative anymore and may lose access to its KPIs and KPI subscriptions.
                  </p>
                </>
              ) : (
                <>
                  <p className={cn("text-sm font-medium mb-3", colors.textPrimary)}>
                    Switching to Epics mode will:
                  </p>
                  <ul className={cn("text-sm mb-4 space-y-1", colors.textSecondary)}>
                    <li>• Delete all <strong>{ownedTags.length}</strong> initiative-owned tag(s)</li>
                    <li>• Remove tags from all associated features</li>
                    <li>• Remove KPI alignments that depend on these tags</li>
                  </ul>
                  <p className={cn("text-sm", colors.textSecondary)}>
                    Progress will then be tracked based on linked epics instead.
                  </p>
                </>
              )}
              <p className={cn("text-xs mt-4", colors.textMuted)}>
                This action cannot be undone.
              </p>
            </div>
            <div className={cn("p-4 border-t flex justify-end gap-3", colors.borderPrimary)}>
              <Button
                variant="outline"
                onClick={cancelModeSwitch}
                className={colors.borderPrimary}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmModeSwitch}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                Switch to {pendingFollowType === 'tags' ? 'Tags' : 'Epics'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Status Change Validation Modal */}
      {showActivationValidation && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowActivationValidation(false)} />
          <div className={cn("relative z-10 p-6 rounded-lg shadow-xl max-w-md w-full mx-4", colors.bgCard)}>
            <h3 className={cn("text-lg font-semibold mb-2 flex items-center gap-2", colors.textPrimary)}>
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Cannot Change Status
            </h3>
            <p className={cn("text-sm mb-4", colors.textSecondary)}>
              Please address the following before moving this initiative out of draft:
            </p>
            <ul className="space-y-2 mb-4">
              {activationErrors.map((error, idx) => (
                <li key={idx} className={cn("text-sm p-2 rounded", colors.bgTertiary)}>
                  <span className={cn("font-medium", colors.textPrimary)}>{error.title}</span>
                  <p className={cn("text-xs mt-0.5", colors.textSecondary)}>{error.description}</p>
                </li>
              ))}
            </ul>
            <div className="flex justify-end">
              <Button onClick={() => setShowActivationValidation(false)}>
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Unsaved Changes Warning */}
      {showUnsavedWarning && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowUnsavedWarning(false)} />
          <div className={cn("relative z-10 p-6 rounded-lg shadow-xl max-w-md w-full mx-4", colors.bgCard)}>
            <h3 className={cn("text-lg font-semibold mb-2", colors.textPrimary)}>Unsaved Changes</h3>
            <p className={cn("text-sm mb-4", colors.textSecondary)}>
              You have unsaved changes. Do you want to save them before closing?
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowUnsavedWarning(false)}>
                Cancel
              </Button>
              <Button variant="ghost" onClick={performClose}>
                Discard
              </Button>
              <Button onClick={handleSaveAndClose} className={cn(colors.accentGradient, "text-white")}>
                Save & Close
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Tag Modal */}
      {showCreateTagModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50">
          <div className={cn("w-full max-w-sm mx-4 rounded-lg shadow-xl", colors.bgCard)}>
            <div className={cn("p-4 border-b flex items-center justify-between", colors.borderPrimary)}>
              <h3 className={cn("font-semibold", colors.textPrimary)}>Create Initiative Tag</h3>
              <button
                onClick={() => {
                  setShowCreateTagModal(false);
                  setNewTagName('');
                  setNewTagColor('#3B82F6');
                }}
                className={cn("p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700", colors.textMuted)}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <Label className={cn("text-sm", colors.textSecondary)}>Tag Name</Label>
                <Input
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="Enter tag name..."
                  className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                  autoFocus
                />
              </div>
              <div>
                <Label className={cn("text-sm", colors.textSecondary)}>Color</Label>
                <div className="flex items-center gap-3 mt-1">
                  <input
                    type="color"
                    value={newTagColor}
                    onChange={(e) => setNewTagColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border"
                  />
                  <span className={cn("text-sm", colors.textMuted)}>{newTagColor}</span>
                </div>
              </div>
            </div>
            <div className={cn("p-4 border-t flex justify-end gap-2", colors.borderPrimary)}>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateTagModal(false);
                  setNewTagName('');
                  setNewTagColor('#3B82F6');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={createOwnedTag}
                disabled={!newTagName.trim()}
                className={cn(colors.accentGradient, "text-white")}
              >
                Create Tag
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Tag Confirmation Modal */}
      {deletingTagId && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50">
          <div className={cn("w-full max-w-sm mx-4 rounded-lg shadow-xl", colors.bgCard)}>
            <div className={cn("p-4 border-b", colors.borderPrimary)}>
              <h3 className={cn("font-semibold flex items-center gap-2", colors.textPrimary)}>
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Delete Tag
              </h3>
            </div>
            <div className="p-4">
              <p className={cn("text-sm", colors.textSecondary)}>
                This tag is currently used by features. Deleting it will:
              </p>
              <ul className={cn("text-sm mt-2 space-y-1", colors.textMuted)}>
                <li>• Remove the tag from all features</li>
                <li>• Remove any KPI alignments that depend on this tag</li>
              </ul>
              <p className={cn("text-sm mt-3 font-medium", colors.textPrimary)}>
                This action cannot be undone.
              </p>
            </div>
            <div className={cn("p-4 border-t flex justify-end gap-2", colors.borderPrimary)}>
              <Button
                variant="outline"
                onClick={() => setDeletingTagId(null)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => deleteOwnedTag(deletingTagId, true)}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Delete Tag
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
    </ModalZIndexProvider>
  );
};

export default InitiativeDetailModal;
